import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { ToWords } from "to-words";
const logo = require("../Images/FullLogo_NoBuffer__4_-removebg-preview.png");


const cleanText = (text, maxLength = 60) => {
  if (!text) return "";
  return text
    .replace(/[^\w\s\-.,:]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .substring(0, maxLength);
};

const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    paddingTop: 25,
    paddingLeft: 25,
    paddingRight: 25,
    
    paddingBottom: 95,
    fontFamily: "Helvetica",
    lineHeight: 1.4,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: "#047857",
    paddingBottom: 10,
  },
  companySection: {
    flex: 2,
  },
  invoiceInfo: {
    flex: 1,
    alignItems: "flex-end",
  },
  logo: { width: 120 },
  companyName: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#333",
    marginTop: 5,
    marginBottom: 5,
  },
  companyDetails: {
    fontSize: 9,
    color: "#666",
    marginTop: 4,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#047857",
    marginVertical: 10,
  },
  table: {
    width: "100%",
    marginVertical: 5,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    alignItems: "center",
    minHeight: 24,
  },
  tableColHeader: {
    width: "20%",
    fontWeight: "bold",
    backgroundColor: "#f0fdf4",
    paddingVertical: 8,
    paddingHorizontal: 6,
    color: "#333",
  },
  tableCol: {
    width: "20%",
    paddingVertical: 5,
    paddingHorizontal: 4,
    color: "#444",
  },
  scheduleSection: {
    backgroundColor: "#f0fdf4",
    borderRadius: 6,
    padding: 12,
    borderWidth: 1,
    borderColor: "#22c55e",
    marginVertical: 8,
  },
  scheduleDay: {
    marginBottom: 8,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: "#dcfce7",
  },
  dayHeader: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#166534",
    marginBottom: 3,
  },
  dayDetails: {
    fontSize: 9,
    lineHeight: 1.4,
    color: "#15803d",
  },
  benefitsSection: {
    backgroundColor: "#ecfdf5",
    borderRadius: 6,
    padding: 10,
    borderWidth: 1,
    borderColor: "#10b981",
    marginVertical: 6,
  },
  benefitsTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#047857",
    marginBottom: 6,
  },
  benefitItem: {
    fontSize: 9,
    marginBottom: 2,
    paddingLeft: 8,
  },
  inclusionsSection: {
    backgroundColor: "#f0fdfa",
    borderRadius: 6,
    padding: 10,
    borderWidth: 1,
    borderColor: "#14b8a6",
    marginVertical: 6,
  },
  inclusionsTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#0d9488",
    marginBottom: 6,
  },
  inclusionItem: {
    fontSize: 9,
    marginBottom: 2,
    paddingLeft: 8,
  },
  amountSection: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 15,
  },
  totalAmount: {
    width: "35%",
    borderWidth: 2,
    borderColor: "#047857",
    padding: 10,
    backgroundColor: "#f0fdf4",
    borderRadius: 4,
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 25,
    right: 25,
    fontSize: 8,
    color: "#666",
    textAlign: "center",
    borderTopWidth: 1,
    borderTopColor: "#eee",
    paddingTop: 8,
  },
  longCol: {
    width: "75%",
    paddingVertical: 8,
    paddingHorizontal: 6,
    color: "#444",
  },
  exclusionsSection: {
    backgroundColor: "#fef2f2",
    borderRadius: 6,
    padding: 10,
    borderWidth: 1,
    borderColor: "#f87171",
    marginVertical: 6,
  },
  exclusionsTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#dc2626",
    marginBottom: 6,
  },
  exclusionItem: {
    fontSize: 9,
    marginBottom: 2,
    paddingLeft: 8,
  },
  guestTableColHeader: {
    width: "25%",
    fontWeight: "bold",
    backgroundColor: "#f0fdf4",
    paddingVertical: 8,
    paddingHorizontal: 6,
    color: "#333",
  },

  guestTableCol: {
    width: "25%",
    paddingVertical: 5,
    paddingHorizontal: 4,
    color: "#444",
  },

  programTableColHeader: {
    width: "20%",
    fontWeight: "bold",
    backgroundColor: "#f0fdf4",
    paddingVertical: 8,
    paddingHorizontal: 6,
    color: "#333",
  },

  programTableCol: {
    width: "20%",
    paddingVertical: 5,
    paddingHorizontal: 4,
    color: "#444",
  },
});

const toWords = new ToWords();
const convertToWords = (amount) => {
  if (!amount || isNaN(amount)) return "Zero Rupees";
  return toWords.convert(amount, { currency: true, ignoreDecimal: true });
};

const getBookingRef = (bookingData) => {
  return bookingData?.bookingReference || "TGH-WBK-0001";
};

const getInvoiceNumber = (bookingData) => {
  return bookingData?.invoiceNumber || "TGH-INV-0001";
};

const WellnessInvoicePDF = ({ invoiceData }) => {
  if (!invoiceData) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text>No invoice data available.</Text>
        </Page>
      </Document>
    );
  }

  const program = invoiceData.packageId && invoiceData.packageId.benefits
    ? invoiceData.packageId
    : {
        benefits: invoiceData.benefits || [],
        inclusions: invoiceData.inclusions || [],
        exclusions: invoiceData.exclusions || [],
        schedule: invoiceData.schedule || [],
        title: invoiceData.packageTitle,
        duration: invoiceData.duration,
        category: invoiceData.category || 'wellness'
      };

  const bookingRef = getBookingRef(invoiceData);
   const invoiceNumber = getInvoiceNumber(invoiceData); 
  const programTitle = invoiceData.packageTitle || program.title || "Wellness Program";
  const programDuration = invoiceData.duration || program.duration || "N/A";

  const travelDate = invoiceData.startDate || invoiceData.preferredDate
    ? new Date(invoiceData.startDate || invoiceData.preferredDate).toLocaleDateString("en-IN", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "N/A";

  const guests = invoiceData.numberOfGuests || invoiceData.guests || 1;

  const accommodationType =
    invoiceData?.accommodation?.type || "No Accommodation";

  const occupancyType =
    invoiceData?.accommodation?.occupancy || "";

  const formatIndianNumber = (num) => {
    if (!num || isNaN(num)) return "0";
    return num.toString().replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  };

  const totalBookingAmount = Number(
    invoiceData?.totalPrice ||
    invoiceData?.finalAmount ||
    invoiceData?.final_price ||
    0
  );

  const amountPaidNow = Number(
    invoiceData?.amountPaid ||
    invoiceData?.advanceAmount ||
    0
  );

  const remainingAmount = Number(
    invoiceData?.remainingAmount ||
    Math.max(totalBookingAmount - amountPaidNow, 0)
  );

  const isPartialPayment =
    invoiceData?.paymentType === "partial" ||
    invoiceData?.paymentStatus === "partially_paid";

  const gstRate = Number(
    invoiceData?.pricing?.gst_price ||
    invoiceData?.pricing?.data?.gst_price ||
    invoiceData?.pricing?.gst ||
    invoiceData?.gstRate ||
    invoiceData?.gst_rate ||
    0
  );

  let baseAmount = totalBookingAmount;
  let gstAmount = 0;

  if (gstRate > 0 && totalBookingAmount > 0) {
    baseAmount = Math.round(totalBookingAmount / (1 + gstRate / 100));
    gstAmount = totalBookingAmount - baseAmount;
  }

  const finalTotal = totalBookingAmount;

  const packageData = invoiceData.packageId && typeof invoiceData.packageId === 'object' && (invoiceData.packageId.title || invoiceData.packageId.benefits)
    ? invoiceData.packageId
    : {};

 
  const formattedSchedule = packageData.schedule?.map((day) => ({
    day: day.day || "Day 1",
    title: cleanText(day.title || "Day Activity"),
    description: cleanText(day.description || "Details coming soon", 150),
  })) || [];

  const formattedBenefits = packageData.benefits?.map((item) => cleanText(item, 50)) || [];
  const formattedInclusions = packageData.inclusions?.map((item) => cleanText(item, 50)) || [];
  const formattedExclusions = packageData.exclusions?.map((item) => cleanText(item, 50)) || [];

  const guestName = invoiceData.name ||
    `${invoiceData.firstName || ''} ${invoiceData.lastName || ''}`.trim() ||
    "Guest";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.companySection}>
            <Image style={styles.logo} src={logo} />
            <Text style={styles.companyName}>The Great Himalayas</Text>
            <Text style={styles.companyDetails}>
              Siliguri, Darjeeling(Dist), West Bengal, 734001{"\n"}
              GSTIN: 09AAHCH9024K1Z7{"\n"}
              Phone: +91 98835 87965 | Email: contact@thegreathimalayas.com
            </Text>
          </View>

<View style={styles.invoiceInfo}>
            <Text style={{ ...styles.sectionTitle, marginTop: 0 }}>
              WELLNESS BOOKING CONFIRMATION
            </Text>
            <Text style={styles.companyDetails}>
              Invoice No: {getInvoiceNumber(invoiceData)}
              {"\n"}
              Booking Ref: {getBookingRef(invoiceData)}
              {"\n"}
              Date: {new Date().toLocaleDateString()}
            </Text>
          </View>
        </View>

        {/* Guest Information */}
        <Text style={styles.sectionTitle}>Guest Information</Text>
        <View style={styles.table} wrap={false}>
          <View style={styles.tableRow}>
            <Text style={styles.guestTableColHeader}>Guest Name</Text>
            <Text style={styles.guestTableColHeader}>Contact</Text>
            <Text style={styles.guestTableColHeader}>Program Date</Text>
            <Text style={styles.guestTableColHeader}>Guests</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.guestTableCol}>{guestName}</Text>
            <Text style={styles.guestTableCol}>
              {invoiceData.email}
              {"\n"}
              {invoiceData.phone}
            </Text>
            <Text style={styles.guestTableCol}>{travelDate}</Text>
            <Text style={styles.guestTableCol}>{guests}</Text>
          </View>
        </View>

        {/* Program Details */}
        <Text style={styles.sectionTitle}>Program Details</Text>
        <View style={styles.table} wrap={false}>
          <View style={styles.tableRow}>
            <Text style={styles.tableColHeader}>Program Name</Text>
            <Text style={styles.tableColHeader}>Category</Text>
            <Text style={styles.tableColHeader}>Duration</Text>
            <Text style={styles.tableColHeader}>Accommodation</Text>
            <Text style={styles.tableColHeader}>Special Requests</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableCol}>
              {cleanText(programTitle) || "N/A"}
            </Text>
            <Text style={styles.tableCol}>
              {cleanText(program.category) || "Wellness"}
            </Text>
            <Text style={styles.tableCol}>{programDuration}</Text>
            <Text style={styles.tableCol}>
              {accommodationType !== "No Accommodation"
                ? `${cleanText(accommodationType)} ${occupancyType ? `(${occupancyType})` : ""}`
                : "Program Only (No Stay)"}
            </Text>
            <Text style={styles.tableCol}>
              {cleanText(invoiceData.specialRequirements || invoiceData.specialRequests) || "-"}
            </Text>
          </View>
        </View>

        {/* Program Schedule Section - all days shown, whole block kept together */}
        {formattedSchedule.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Program Schedule</Text>
            <View style={styles.scheduleSection} wrap={false}>
              {formattedSchedule.map((day, index) => (
                <View key={index} style={styles.scheduleDay}>
                  <Text style={styles.dayHeader}>
                    {day.day}: {day.title}
                  </Text>
                  <Text style={styles.dayDetails}>{day.description}</Text>
                </View>
              ))}
            </View>
          </>
        )}

        {/* Benefits Section - all items shown, whole block kept together */}
        {formattedBenefits.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Program Benefits</Text>
            <View style={styles.benefitsSection} wrap={false}>
              <Text style={styles.benefitsTitle}>What You Will Experience</Text>
              {formattedBenefits.map((benefit, index) => (
                <Text key={index} style={styles.benefitItem}>
                  • {benefit}
                </Text>
              ))}
            </View>
          </>
        )}

        {/* Inclusions Section - all items shown, whole block kept together */}
        {formattedInclusions.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Whats Included</Text>
            <View style={styles.inclusionsSection} wrap={false}>
              <Text style={styles.inclusionsTitle}>Package Inclusions</Text>
              {formattedInclusions.map((inclusion, index) => (
                <Text key={index} style={styles.inclusionItem}>
                  • {inclusion}
                </Text>
              ))}
            </View>
          </>
        )}

        {/* Exclusions Section - all items shown, whole block kept together */}
        {formattedExclusions.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>What's NOT Included</Text>
            <View style={styles.exclusionsSection} wrap={false}>
              <Text style={styles.exclusionsTitle}>Exclusions</Text>
              {formattedExclusions.map((exclusion, index) => (
                <Text key={index} style={styles.exclusionItem}>
                  • {exclusion}
                </Text>
              ))}
            </View>
          </>
        )}

        {/* Payment Details Section */}
        <Text style={styles.sectionTitle}>Payment Details</Text>
        <View style={styles.table} wrap={false}>
          <View style={styles.tableRow}>
            <Text style={styles.longCol}>Description</Text>
            <Text style={styles.tableColHeader}>Amount (₹)</Text>
          </View>

          {/* Row: Total Booking Amount */}
          <View style={styles.tableRow}>
            <Text style={styles.longCol}>Total Booking Amount</Text>
            <Text style={styles.tableCol}>₹ {totalBookingAmount.toLocaleString("en-IN")}</Text>
          </View>

          {/* Conditional Rows: Show Advance Paid vs Full Payment */}
          {isPartialPayment ? (
            <>
              <View style={styles.tableRow}>
                <Text style={styles.longCol}>Advance Paid</Text>
                <Text style={styles.tableCol}>₹ {amountPaidNow.toLocaleString("en-IN")}</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.longCol}>Remaining Balance Due</Text>
                <Text style={styles.tableCol}>₹ {remainingAmount.toLocaleString("en-IN")}</Text>
              </View>
            </>
          ) : (
            <View style={styles.tableRow}>
              <Text style={styles.longCol}>Amount Paid</Text>
              <Text style={styles.tableCol}>₹ {amountPaidNow.toLocaleString("en-IN")}</Text>
            </View>
          )}
        </View>

        {/* Grand Total Box - kept together as a single unit */}
        <View style={styles.amountSection} wrap={false}>
          <View style={styles.totalAmount}>
            <Text style={{ fontWeight: "bold", fontSize: 12 }}>
              {isPartialPayment ? "BOOKING TOTAL" : "GRAND TOTAL"}
            </Text>
            <Text style={{ fontSize: 16, marginTop: 4 }}>
              ₹ {totalBookingAmount.toLocaleString("en-IN")}
            </Text>
            <Text style={{ fontSize: 9, marginTop: 4 }}>
              {convertToWords(totalBookingAmount)} Only
            </Text>
            {isPartialPayment && (
              <Text style={{ fontSize: 9, marginTop: 6, color: "#dc2626", fontWeight: "bold" }}>
                Balance Due: ₹ {remainingAmount.toLocaleString("en-IN")}
              </Text>
            )}
          </View>
        </View>

        {/* Footer - `fixed` makes it render correctly on every page at the same
            position, and the page's paddingBottom above reserves space so
            flowing content never runs underneath it */}
        <View style={styles.footer} fixed>
          <Text>
            Thank you for choosing The Great Himalayas Hotels! {'\n'}

            • Cancellation Policy:{'\n'}
            100% refund if cancelled 72 hours before check-in •
            50% refund if cancelled between 24 to 72 hours •
            No refund within 24 hours of check-in {'\n'}

            • For queries: +91 98835 87965 | contact@thegreathimalayas.com {'\n'}
            • This is a computer generated invoice • Terms & Conditions apply
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default WellnessInvoicePDF;
